/* global rwgps */
import React from 'react'
import styles from './Metrics.scss'
import {OrderedMap} from 'immutable'
import moment from 'moment'
import {formatDistance, formatSpeed, formatGrade, formatDuration, formatPace} from 'lib/units'
import {getConsole} from 'lib/getConsole'
const console = getConsole('common', 'Metrics')

const formatTime = (timestampSeconds) => {
  const m = moment(timestampSeconds * 1000)
  const val = m.format('h:mm')
  const amPm = m.format('a')
  return {
    valueToString: () => val,
    short: amPm,
    long: amPm
  }
}

const formatVAM = (vam) => ({
  short: '',
  long: 'Vm/h',
  valueToString: () => vam
})

const tripMetrics = {
  minSpeed: {
    label: 'Min Speed',
    value: (mcResult, metricUnits) => formatSpeed(mcResult.speed.min, {metricUnits})
  },
  maxSpeed: {
    label: 'Max Speed',
    value: (mcResult, metricUnits) => formatSpeed(mcResult.speed.max, {metricUnits})
  },
  avgSpeed: {
    label: 'Avg Speed',
    value: (mcResult, metricUnits) => formatSpeed(mcResult.speed.avg, {metricUnits})
  },
  duration: {
    label: 'Duration',
    value: (mcResult, metricUnits) => formatDuration(mcResult.duration)
  },
  startTime: {
    label: 'Start Time',
    value: (mcResult, metricUnits) => formatTime(mcResult.firstTime)
  },
  endTime: {
    label: 'End Time',
    value: (mcResult, metricUnits) => formatTime(mcResult.firstTime + mcResult.duration)
  },
  movingTime: {
    label: 'Moving Time',
    value: (mcResult, metricUnits) => formatDuration(mcResult.movingTime)
  },
  stoppedTime: {
    label: 'Stopped Time',
    value: (mcResult, metricUnits) => formatDuration(mcResult.stoppedTime)
  },
  pace: {
    label: 'Pace',
    value: (mcResult, metricUnits) => formatPace(mcResult.pace, {metricUnits})
  },
  movingPace: {
    label: 'Moving Pace',
    value: (mcResult, metricUnits) => formatPace(mcResult.movingPace, {metricUnits})
  },
  ascentTime: {
    label: 'Ascent Time',
    value: (mcResult, metricUnits) => formatDuration(mcResult.ascentTime)
  },
  descentTime: {
    label: 'Descent Time',
    value: (mcResult, metricUnits) => formatDuration(mcResult.descentTime)
  },
  vam: {
    label: 'VAM',
    value: (mcResult, metricUnits) => formatVAM(mcResult.vam.toFixed(0))
  }
}

const trouteMetrics = {
  minEle: {
    label: 'Min Elevation',
    value: (mcResult, metricUnits) => formatDistance(mcResult.ele.min, {metricUnits, smallUnits: true})
  },
  maxEle: {
    label: 'Max Elevation',
    value: (mcResult, metricUnits) => formatDistance(mcResult.ele.max, {metricUnits, smallUnits: true})
  },
  avgEle: {
    label: 'Avg Elevation',
    value: (mcResult, metricUnits) => formatDistance(mcResult.ele.avg, {metricUnits, smallUnits: true})
  },
  minGrade: {
    label: 'Min Grade',
    value: (mcResult, metricUnits) => formatGrade(mcResult.grade.min)
  },
  maxGrade: {
    label: 'Max Grade',
    value: (mcResult, metricUnits) => formatGrade(mcResult.grade.max)
  },
  avgGrade: {
    label: 'Avg Grade',
    value: (mcResult, metricUnits) => formatGrade(mcResult.grade.avg)
  },
  distance: {
    label: 'Distance',
    value: (mcResult, metricUnits) => formatDistance(mcResult.distance, {metricUnits})
  },
  startEle: {
    label: 'Start Elevation',
    value: (mcResult, metricUnits) => formatDistance(mcResult.startElevation, {metricUnits, smallUnits: true})
  },
  endEle: {
    label: 'End Elevation',
    value: (mcResult, metricUnits) => formatDistance(mcResult.endElevation, {metricUnits, smallUnits: true})
  },
  eleGain: {
    label: 'Elevation Gain',
    value: (mcResult, metricUnits) => formatDistance(mcResult.ele_gain, {metricUnits, smallUnits: true})
  },
  eleLoss: {
    label: 'Elevation Loss',
    value: (mcResult, metricUnits) => formatDistance(mcResult.ele_loss, {metricUnits, smallUnits: true})
  }
}

export const metricsSpec = OrderedMap({...tripMetrics, ...trouteMetrics})

export const routeMetricsSpec = OrderedMap({...trouteMetrics})

// Metrics display component, will probably be its own blocktype as well at some
// point so I'm trying to not couple this to the Map tightly
export class Metrics extends React.PureComponent {
  render () {
    const {ride, highlightSlice, metricsToShow, metricUnits} = this.props
    // keeping this around so that we get lots of warning if something causes
    // this (comparatively expensive) re-render
    console.log(`⚠️ recalculated metrics (ride id = ${ride.get('id')})`)
    const tpsResult = rwgps.TrackPoint.fromJSON(ride.rawPoints)
    const points = tpsResult.trackPoints
    const data = new rwgps.Data(points)
    const dataSlice = highlightSlice && data.slice(highlightSlice[0], highlightSlice[1] - highlightSlice[0])

    const toCalculate = highlightSlice
      ? dataSlice
      : data

    // in our case, setting readonly to false can't cause problems here because
    // the source data is immutable
    const mc = new rwgps.MetricsCalculator(toCalculate, false)
    const mcResult = mc.calculate()

    return (
      <div className={styles.Metrics}>
        {metricsToShow.map(k => (
          <Metric metric={metricsSpec.get(k)} metricUnits={metricUnits} calculatedMetrics={mcResult} key={k} />
        ))}
      </div>
    )
  }
}

// // temp hack
// export class Metrics extends React.PureComponent {
//   shouldComponentUpdate (nextProps) {
//     const props = this.props
//     console.log('shouldComponentUpdate metrics')
//     console.log('nextProps.ride === props.ride', nextProps.ride === props.ride)
//     console.log('nextProps.highlightSlice === props.highlightSlice', nextProps.highlightSlice === props.highlightSlice)
//     // metricsToShow is the culprit
//     console.log('nextProps.metricsToShow === props.metricsToShow', nextProps.metricsToShow === props.metricsToShow)
//   }

//   render () {
//     return Metrics_(this.props)
//   }
// }

const Metric = ({metric, calculatedMetrics, metricUnits}) => {
  const formatted = metric.value(calculatedMetrics, metricUnits)
  return (
    <div className={styles.Metric}>
      <h4>{metric.label}</h4>
      <p><span className={styles.metricValue}>{formatted.valueToString()}</span> <span className={styles.metricUnits}>{formatted.short}</span></p>
    </div>
  )
}
